import {createSelector} from 'reselect';
import type {
  Interaction,
  PageScope,
  Timeline,
  TriggerConfig,
} from '@packages/systems/ix3/core';
import {TimelineControlType} from '@packages/systems/ix3/core';
import type {IX3State} from './types';
import {arrayOrEmpty} from './utils';
import {ActionViewState} from './constants';
import {IX3_WF_EXTENSION_KEYS} from '@packages/systems/ix3-wf-plugins/shared';
import {
  collectExternalReuseSources,
  type TimelineAction,
} from '@packages/systems/ix3/schema';

// ============================================================================
// Interactions State
// ============================================================================
export const selectInteractions = createSelector(
  (store: IX3State) => store.interactions,
  (interactions) =>
    Object.fromEntries(
      Object.entries(interactions).filter(
        ([, interaction]) => !interaction.deleted
      )
    )
);

/** select all interactions, including deleted ones (used in IX3sync) */
export const selectAllInteractions = (store: IX3State) => store.interactions;

export const selectTimelines = createSelector(
  (store: IX3State) => store.timelines,
  (timelines) =>
    Object.fromEntries(
      Object.entries(timelines).filter(([, timeline]) => !timeline.deleted)
    )
);

export const selectAllTimelines = (store: IX3State) => store.timelines;

export const selectActionPresets = createSelector(
  (store: IX3State) => store.actionPresets,
  (actionPresets) =>
    Object.fromEntries(
      Object.entries(actionPresets).filter(
        ([, actionPreset]) => !actionPreset.deleted
      )
    )
);

export const selectAllActionPresets = (store: IX3State) => store.actionPresets;

export const selectSelectedInteractionId = (store: IX3State) =>
  store.selectedInteractionId;

export const selectHasInteractions = createSelector(
  selectInteractions,
  (interactions) => {
    const firstInteraction = Object.values(interactions)[0];
    /**
     * Not only should there be at least 1 interaction,
     * but the first interaction should have at least 1 timeline.
     * This happens after the user progresses past the "Add action" step (not skippable).
     */
    return firstInteraction ? firstInteraction?.timelineIds.length > 0 : false;
  }
);

export const selectSelectedInteraction = createSelector(
  selectInteractions,
  selectSelectedInteractionId,
  (interactions, selectedInteractionId): Interaction | null =>
    (selectedInteractionId && interactions[selectedInteractionId]) || null
);

export const selectSelectedInteractionPageScope = createSelector(
  selectSelectedInteraction,
  (selectedInteraction): PageScope =>
    selectedInteraction?.scope || {type: 'site'}
);

export const selectSelectedInteractionVariantScope = createSelector(
  selectSelectedInteraction,
  (selectedInteraction): string[] | undefined => {
    if (selectedInteraction?.scope?.type !== 'component') return undefined;
    return selectedInteraction.scope.variants;
  }
);

export const selectSelectedInteractionTimelineDefaults = createSelector(
  selectSelectedInteraction,
  (selectedInteraction) => selectedInteraction?.timelineDefaults || null
);

export const selectSelectedInteractionConditionalPlayback = createSelector(
  selectSelectedInteraction,
  (selectedInteraction) => selectedInteraction?.conditionalPlayback || null
);

/**
 * Selector for the currently selected timeline index (for multi-timeline interactions).
 * Used for triggers like Mouse Move (X/Y) and Navbar/Dropdown (open/close).
 */
export const selectSelectedTimelineIndex = (store: IX3State) =>
  store.uiSelectedTimelineIndex;

/**
 * Get all timelines for the selected interaction.
 * Returns an array of timeline IDs in order.
 */
export const selectSelectedInteractionTimelineIds = createSelector(
  selectSelectedInteraction,
  (selectedInteraction) => selectedInteraction?.timelineIds || []
);

/**
 * Whether the selected interaction has multiple timelines.
 * Used to determine if timeline tabs should be shown.
 */
export const selectHasMultipleTimelines = createSelector(
  selectSelectedInteractionTimelineIds,
  (timelineIds) => timelineIds.length > 1
);

/**
 * Get the currently selected timeline ID based on the selected index.
 * Supports both single-timeline (backwards compatible) and multi-timeline interactions.
 */
export const selectSelectedTimelineId = createSelector(
  selectSelectedInteraction,
  selectSelectedTimelineIndex,
  (selectedInteraction, selectedIndex) => {
    if (!selectedInteraction) return null;

    const {timelineIds} = selectedInteraction;
    if (timelineIds.length === 0) return null;

    // Clamp index to valid range
    const safeIndex = Math.min(selectedIndex, timelineIds.length - 1);
    return timelineIds[safeIndex] || null;
  }
);

export const selectSelectedTimeline = createSelector(
  selectTimelines,
  selectSelectedTimelineId,
  (timelines, timelineId) => (timelineId && timelines[timelineId]) || null
);

export const selectSelectedTimelineRole = createSelector(
  selectSelectedTimeline,
  (selectedTimeline) => selectedTimeline?.triggerMetadata?.role
);

/**
 * Whether the selected timeline has been persisted yet. Scroll-scrub timelines
 * are created lazily — there is no timeline object until the first action is
 * added — so this distinguishes "no timeline yet" (a new scroll-scrub
 * interaction) from "an existing timeline that simply has no `canvasDuration`
 * marker" (a legacy timeline authored before the percent canvas existed). The
 * percent-canvas editor uses it to keep legacy scroll timelines time-based.
 */
export const selectHasSelectedTimeline = createSelector(
  selectSelectedTimeline,
  (selectedTimeline) => selectedTimeline != null
);

export const selectSelectedTimelineSettings = createSelector(
  selectSelectedTimeline,
  (selectedTimeline) => selectedTimeline?.settings || null
);

/**
 * Nominal percent-canvas length (seconds) of the selected timeline, or null.
 *
 * Present only on gesture-driven (scroll-scrub / mouse-move) timelines authored
 * under the percent-timeline flag. When set, the editor renders the ruler and
 * the duration/start inputs as percent (0–100%) over this length; absent means
 * the timeline keeps content-derived seconds behavior.
 */
export const selectTimelineCanvasDuration = createSelector(
  selectSelectedTimeline,
  (selectedTimeline) => selectedTimeline?.canvasDuration ?? null
);

/**
 * Get actions for the currently selected timeline only.
 * This respects the multi-timeline selection (uiSelectedTimelineIndex).
 * Use this for the horizontal timeline UI and anywhere that should show
 * only the actions from the active timeline.
 */
export const selectCurrentTimelineActions = createSelector(
  selectSelectedTimeline,
  (selectedTimeline) => {
    if (!selectedTimeline) {
      return [];
    }
    return selectedTimeline.actions || [];
  }
);

/**
 * Get actions from the selected interaction.
 * For single-timeline interactions: returns all actions (backwards compatible).
 * For multi-timeline interactions: respects uiSelectedTimelineIndex and returns
 * only actions from the currently selected timeline.
 */
export const selectSelectedInteractionActions = createSelector(
  selectSelectedInteraction,
  selectTimelines,
  selectSelectedTimelineIndex,
  (selectedInteraction, timelines, selectedTimelineIndex) => {
    if (!selectedInteraction) {
      return [];
    }

    const {timelineIds} = selectedInteraction;

    // Single-timeline: return all actions (backwards compatible)
    if (timelineIds.length <= 1) {
      return timelineIds.flatMap((timelineId) =>
        arrayOrEmpty(timelines[timelineId]?.actions)
      );
    }

    // Multi-timeline: respect uiSelectedTimelineIndex
    const safeIndex = Math.min(selectedTimelineIndex, timelineIds.length - 1);
    const selectedTimelineId = timelineIds[safeIndex];
    if (!selectedTimelineId) {
      return [];
    }

    return arrayOrEmpty(timelines[selectedTimelineId]?.actions);
  }
);

export const selectSelectedInteractionTimelines = createSelector(
  selectSelectedInteraction,
  selectTimelines,
  (selectedInteraction, timelines) => {
    const selectedTimelines: Timeline[] = [];
    if (!selectedInteraction) {
      return selectedTimelines;
    }

    for (const timelineId of selectedInteraction.timelineIds) {
      const timeline = timelines[timelineId];
      if (timeline) selectedTimelines.push(timeline);
    }
    return selectedTimelines;
  }
);

/**
 * The live timelines outside the selected interaction that its timelines
 * reuse, directly or through a chain. The runtime plays a `reuse` alias's
 * source wherever it lives, so these dispatch their actions for the selected
 * interaction's triggers; only code view can author one. The walk follows 12
 * links: IX3Engine redirects an alias to its source once, then
 * AnimationCoordinator's alias resolution follows 11 more. It also ends a
 * cycle.
 */
export const selectSelectedInteractionExternalReuseSources = createSelector(
  selectSelectedInteraction,
  selectTimelines,
  (selectedInteraction, timelines) =>
    selectedInteraction
      ? collectExternalReuseSources(selectedInteraction.timelineIds, timelines)
      : []
);

/**
 * Returns actions from all sibling timelines (not the currently selected one).
 * Used for conflict detection in continuous trigger interactions (e.g., Mouse Move).
 */
export const selectSiblingTimelinesActions = createSelector(
  selectSelectedInteractionTimelines,
  selectSelectedTimelineIndex,
  (timelines, selectedIndex): TimelineAction[] =>
    timelines.flatMap((timeline, index) =>
      index === selectedIndex ? [] : timeline.actions
    )
);

// Get timelines for a specific interaction, not specifically the one that selected in state
export const selectInteractionTimelines = createSelector(
  selectTimelines,
  (_: IX3State, interaction: Interaction) => interaction,
  (timelines, interaction) => {
    return interaction.timelineIds.flatMap(
      (timelineId) => timelines[timelineId] || []
    );
  }
);

export const selectTriggersFromSelectedInteraction = createSelector(
  selectSelectedInteraction,
  (selectedInteraction) => arrayOrEmpty(selectedInteraction?.triggers)
);

export const selectSelectedInteractionPrimaryTriggerExtensionKey =
  createSelector(selectTriggersFromSelectedInteraction, (triggers) => {
    return triggers[0]?.[0];
  });

export const selectSelectedInteractionHasPrimaryPageLoadTrigger =
  createSelector(
    selectSelectedInteractionPrimaryTriggerExtensionKey,
    (primaryTriggerExtensionKey) =>
      primaryTriggerExtensionKey === IX3_WF_EXTENSION_KEYS.LOAD
  );

// Get the selected interaction timeline action (first item in the array)
export const selectSelectedTimelineActionId = (store: IX3State) =>
  store.selectedTimelineActionIds[0] || null;

// Get the multiple selected interaction timeline action IDs
export const selectSelectedTimelineActionIds = (store: IX3State) =>
  store.selectedTimelineActionIds;

export const selectSelectedTimelineAction = createSelector(
  selectSelectedTimeline,
  selectSelectedTimelineActionId,
  (selectedTimeline, selectedTimelineActionId) => {
    if (!selectedTimeline) {
      return null;
    }

    if (!selectedTimeline.actions || selectedTimeline.actions.length === 0) {
      return null;
    }

    const selectedAction =
      selectedTimelineActionId &&
      selectedTimeline.actions.find(
        (action) => action.id === selectedTimelineActionId
      );
    const firstAction = selectedTimeline.actions[0];

    return selectedAction || firstAction || null;
  }
);

export const selectSelectedTimelineActions = createSelector(
  selectSelectedTimeline,
  selectSelectedTimelineActionIds,
  (selectedTimeline, selectedTimelineActionIds) => {
    if (!selectedTimeline) {
      return [];
    }

    const actionsMap = new Map(
      selectedTimeline.actions.map((action) => [action.id, action])
    );

    return selectedTimelineActionIds.flatMap((id) => actionsMap.get(id) || []);
  }
);

/**
 * Get the actions that are currently selected/checked in the UI (for multi-select editing).
 * This returns only the actions whose IDs are in selectedTimelineActionIds.
 * Use selectCurrentTimelineActions to get ALL actions from the selected timeline.
 */
export const selectCheckedActions = createSelector(
  selectSelectedTimeline,
  selectSelectedTimelineActionIds,
  (selectedTimeline, selectedTimelineActionIds) => {
    if (!selectedTimeline) {
      return [];
    }

    const actionsMap = new Map(
      selectedTimeline.actions.map((action) => [action.id, action])
    );

    return selectedTimelineActionIds.flatMap((id) => actionsMap.get(id) || []);
  }
);

// Get the targets from the selected timeline action
export const selectTargetsFromSelectedTimeline = createSelector(
  selectSelectedTimelineAction,
  (selectedTimelineAction) => arrayOrEmpty(selectedTimelineAction?.targets)
);

/**
 * Checks if the selected Interaction has a scroll trigger, and if
 * any scroll triggers have scrub enabled.
 *
 * This is used to adjust the units (duration / position) of the controls
 * and the units that appear in the Timeline UI.
 */
export const selectScrollScrubTriggersFromSelectedInteraction = createSelector(
  selectSelectedInteraction,
  (selectedInteraction) => {
    const scrollTriggers = selectedInteraction?.triggers.filter((trigger) => {
      const [, triggerConfig] = trigger;
      return triggerConfig != null && isScrollTriggerScrub(triggerConfig);
    });

    return arrayOrEmpty(scrollTriggers);
  }
);

export function isScrollTriggerScrub(
  triggerConfig: TriggerConfig<unknown>
): boolean {
  const {controlType, scrollTriggerConfig} = triggerConfig;
  const scrub = scrollTriggerConfig?.scrub;
  return controlType === TimelineControlType.SCROLL && scrub != null;
}

/**
 * Returns true if the selected interaction has a scroll trigger with scrub enabled.
 * Used to disable play controls since scroll interactions are controlled by scrolling.
 */
export const selectHasScrollTriggerWithScrub = createSelector(
  selectSelectedInteraction,
  (selectedInteraction): boolean => {
    if (!selectedInteraction) return false;

    return selectedInteraction.triggers.some((trigger) => {
      const [, triggerConfig] = trigger;
      return triggerConfig != null && isScrollTriggerScrub(triggerConfig);
    });
  }
);

/**
 * Returns true if a trigger config is a continuous control type (e.g., Mouse Move).
 */
export function isContinuousTrigger(
  triggerConfig: TriggerConfig<unknown>
): boolean {
  return triggerConfig.controlType === TimelineControlType.CONTINUOUS;
}

/**
 * Returns true if the selected interaction has a continuous trigger (e.g., Mouse Move).
 * Used to show mouse preview mode instead of playback controls.
 */
export const selectHasContinuousTrigger = createSelector(
  selectSelectedInteraction,
  (selectedInteraction): boolean => {
    if (!selectedInteraction) return false;

    return selectedInteraction.triggers.some((trigger) => {
      const [, triggerConfig] = trigger;
      return triggerConfig != null && isContinuousTrigger(triggerConfig);
    });
  }
);

/**
 * Returns the continuous trigger config if the selected interaction has one.
 * Used to get the trigger target (viewport vs element) for mouse tracking.
 */
export const selectContinuousTriggerConfig = createSelector(
  selectSelectedInteraction,
  (selectedInteraction) => {
    if (!selectedInteraction) return null;

    const trigger = selectedInteraction.triggers.find((t) => {
      const [, triggerConfig] = t;
      return triggerConfig != null && isContinuousTrigger(triggerConfig);
    });

    return trigger ? trigger[1] : null;
  }
);

// ============================================================================
// Timeline / UI State
// ============================================================================
export const selectUIShowAddAction = (store: IX3State) => store.uiShowAddAction;

export const selectIsUiScrubbing = (store: IX3State) => store.uiIsScrubbing;

export const selectUIShowCanvasTargetOutlines = (store: IX3State) =>
  store.uiShowCanvasTargetOutlines;

export const selectUIScrollPreviewMode = (store: IX3State) =>
  store.uiScrollPreviewMode;

/**
 * Gets the effective actions for the selected timeline, following reuse chain.
 * A reusing timeline renders the source timeline's actions — the follower
 * has no own actions and the visual timeline mirrors the source.
 */
export const selectEffectiveTimelineActions = createSelector(
  selectSelectedTimeline,
  selectTimelines,
  (selectedTimeline, allTimelines): TimelineAction[] => {
    if (!selectedTimeline) return [];

    if (selectedTimeline.reuse?.sourceTimelineId) {
      const sourceTimeline =
        allTimelines[selectedTimeline.reuse.sourceTimelineId];
      return sourceTimeline?.actions || [];
    }

    return selectedTimeline.actions || [];
  }
);

/**
 * Returns true when the selected timeline reuses another timeline — i.e. it
 * has no own actions and is meant to play the source verbatim.
 */
export const selectIsTimelineReusing = createSelector(
  selectSelectedTimeline,
  (selectedTimeline): boolean =>
    selectedTimeline?.reuse?.sourceTimelineId != null
);

export const selectActionUiViewState = createSelector(
  selectSelectedTimelineActionIds,
  (selectedTimelineActionIds): ActionViewState => {
    const hasOneActionSelected = selectedTimelineActionIds.length === 1;

    return hasOneActionSelected
      ? ActionViewState.ActionEdit
      : ActionViewState.ActionMain;
  }
);
export const selectUIShowTimelineFlag = (store: IX3State) =>
  store.uiShowTimeline;

export const selectUITimelineHeight = (store: IX3State) =>
  store.uiTimelineHeight;

export const selectUIShowRelativeTimeUnits = createSelector(
  selectScrollScrubTriggersFromSelectedInteraction,
  selectHasContinuousTrigger,
  (scrollScrubTriggers, hasContinuousTrigger) =>
    scrollScrubTriggers.length > 0 || hasContinuousTrigger
);

// ============================================================================
// Variant Cascade Deletion
// ============================================================================

/**
 * Returns interactions affected by deleting a specific variant option.
 * - `toDelete`: interactions scoped to ONLY this variant (will be fully removed)
 * - `toUpdate`: interactions scoped to this variant AND others (will lose this variant from scope)
 */
// ============================================================================
// Timeline Groups
// ============================================================================

/**
 * Get user-managed timeline group info for the selected interaction.
 * Returns only timelines that carry a `groupId` (i.e. user-authored
 * groups on discrete-trigger interactions). Plugin-defined timelines
 * tagged with `triggerMetadata.role` (mouse-move, navbar, etc.) are not
 * included here — those route via callback role, not via the Designer
 * "Controls" dropdown.
 */
export const selectTimelineGroups = createSelector(
  selectSelectedInteraction,
  (store: IX3State) => store.timelines,
  (
    interaction,
    timelines
  ): Array<{
    timelineId: string;
    groupId: string;
    name: string;
  }> => {
    if (!interaction) return [];

    return interaction.timelineIds
      .map((id, index) => {
        const timeline = timelines[id];
        if (!timeline || timeline.deleted) return null;
        const groupId = timeline.groupId;
        if (!groupId) return null;
        return {
          timelineId: id,
          groupId,
          name: timeline.name || `Timeline ${index + 1}`,
        };
      })
      .filter((group): group is NonNullable<typeof group> => group !== null);
  }
);

/**
 * The timeline roles present on the selected interaction, in timeline order.
 * The other routing axis to `selectTimelineGroups`: role-based interactions (a
 * Popover's Open/Close) carry `triggerMetadata.role` instead of a user-managed
 * `groupId`. Labels come from the owning trigger's plugin schema, so this
 * returns bare role names and leaves the naming to the Designer.
 */
export const selectTimelineRoles = createSelector(
  selectSelectedInteraction,
  (store: IX3State) => store.timelines,
  (interaction, timelines): string[] => {
    if (!interaction) return [];

    const roles: string[] = [];
    for (const id of interaction.timelineIds) {
      const timeline = timelines[id];
      if (!timeline || timeline.deleted) continue;
      const role = timeline.triggerMetadata?.role;
      if (role != null && !roles.includes(role)) roles.push(role);
    }
    return roles;
  }
);

// `selectCanAddTimelineGroups` and `selectCanAddMoreTimelineGroups` used
// to live here, hardcoding a list of standalone trigger extension keys
// (SCROLL/MOUSE_MOVE/NAVBAR/DROPDOWN) inside the core store. The decision
// now lives in the Designer-side `useTimelineGroups` hooks, which read
// trigger-plugin metadata (`exclusivity`, `isMultiTimeline`, `controlType`)
// from `IX3Registry`. Keeps the core store free of plugin enumeration.

export function getInteractionsAffectedByVariantDeletion(
  interactions: Record<string, Interaction>,
  variantId: string
): {toDelete: Interaction[]; toUpdate: Interaction[]} {
  const toDelete: Interaction[] = [];
  const toUpdate: Interaction[] = [];

  for (const interaction of Object.values(interactions)) {
    if (interaction.deleted) continue;
    if (interaction.scope?.type !== 'component') continue;
    if (!interaction.scope.variants?.includes(variantId)) continue;

    if (interaction.scope.variants.length === 1) {
      toDelete.push(interaction);
    } else {
      toUpdate.push(interaction);
    }
  }

  return {toDelete, toUpdate};
}
